import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface DemoAccountState {
  isDemo: boolean;
  persona: string | null;
  expiresAt: string | null;
  daysRemaining: number | null;
  expired: boolean;
  loading: boolean;
}

const initialState: DemoAccountState = {
  isDemo: false,
  persona: null,
  expiresAt: null,
  daysRemaining: null,
  expired: false,
  loading: true,
};

/**
 * Reads the demo/trial markers on the signed-in user's profile.
 * Non-demo accounts resolve to `isDemo: false` and cost one cheap query.
 */
export function useDemoAccount(): DemoAccountState {
  const { user } = useAuth();
  const [state, setState] = useState<DemoAccountState>(initialState);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setState({ ...initialState, loading: false });
      return;
    }

    (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('is_demo, demo_persona, demo_expires_at')
        .eq('id', user.id)
        .maybeSingle();

      if (cancelled) return;

      const isDemo = Boolean(data?.is_demo);
      const expiresAt = (data?.demo_expires_at as string | null) ?? null;

      let daysRemaining: number | null = null;
      let expired = false;

      if (isDemo && expiresAt) {
        const ms = new Date(expiresAt).getTime() - Date.now();
        daysRemaining = Math.max(0, Math.ceil(ms / 86400000));
        // Demo access is treated as unlimited for now (preview mode).
        expired = false;
      }

      setState({
        isDemo,
        persona: (data?.demo_persona as string | null) ?? null,
        expiresAt,
        daysRemaining,
        expired,
        loading: false,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  return state;
}

export default useDemoAccount;
